/**
 * PATH: packages/ui/src/kanban/formatDrfError.ts
 * FILE_ID: UI-KANBAN-LIB-001
 * PURPOSE: Turn a DRF error response body into a human-readable string
 *          the UI can show next to the failing form control.
 * WHY: Bug 2026-05-15 — POST /cards/ surfaced as "POST /cards/ failed: 400"
 *      with no actionable detail because the FE only looked for `body.detail`.
 *      DRF emits field-level validation as `{ field: ["msg", ...] }` (no
 *      .detail), so the user had no idea their title was over the 300-char
 *      cap. This helper turns those field maps into "title: max length 300".
 * FLOW:
 *   ┌──────────────────┐     ┌──────────────────┐     ┌───────────────────┐
 *   │ DRF JSON body    │ →   │ formatDrfError() │ →   │ "title: too long" │
 *   └──────────────────┘     └──────────────────┘     └───────────────────┘
 * DEPENDENCIES: none (pure I→O, zero external imports)
 * RELATED:
 *   - api.fetch.ts: consumes this in the !res.ok branch of _request()
 */

/** Format a DRF error response body into a single human-readable string.
 *
 *  Recognised shapes (in priority order):
 *    1. `{ detail: "..." }` → return the detail string verbatim.
 *    2. `{ non_field_errors: [...] }` → flatten the array.
 *    3. `{ field: [...] | "..." }` mapping → "field: msg, msg; field2: msg".
 *    4. Plain string → return as-is.
 *
 *  Returns `null` when the body has no recognisable error shape, so the
 *  caller can fall back to a generic `"{METHOD} {path} failed: {status}"`
 *  message. Never throws — defensive against malformed JSON.
 */
export function formatDrfError(body: unknown): string | null {
  if (body == null) return null
  if (typeof body === 'string') {
    const trimmed = body.trim()
    return trimmed || null
  }
  if (typeof body !== 'object') return null

  const obj = body as Record<string, unknown>

  if (typeof obj.detail === 'string' && obj.detail.trim()) {
    return obj.detail.trim()
  }

  if (Array.isArray(obj.non_field_errors)) {
    const joined = obj.non_field_errors
      .filter((m): m is string => typeof m === 'string' && m.trim().length > 0)
      .join(', ')
    if (joined) return joined
  }

  const parts: string[] = []
  for (const [field, value] of Object.entries(obj)) {
    if (field === 'detail' || field === 'non_field_errors') continue
    // FEAT-2026-05-28-TEAM-ALLOC-HARDENING — the BE attaches a
    // machine-readable `code` sibling alongside field/detail
    // payloads (e.g. `{"user":"...","code":"cross_org"}`). It's
    // metadata for the FE switch statement, NOT a field name to
    // display to the operator — skip it from the human-readable
    // concatenation. ``extractDrfErrorCode()`` below reads it
    // explicitly.
    if (field === 'code') continue
    const msg = _coerceFieldMessage(value)
    if (msg) parts.push(`${field}: ${msg}`)
  }
  return parts.length > 0 ? parts.join('; ') : null
}

/** Pull the structured `code` field out of a DRF error body if
 *  present. The BE uses this as a stable, machine-readable
 *  classifier (`cross_org`, `last_owner`, `invalid_role`, …) so
 *  the FE can render a tailored toast / inline error without
 *  parsing free-text English.
 *
 *  Returns the trimmed code string when present, otherwise null.
 */
export function extractDrfErrorCode(body: unknown): string | null {
  if (body == null || typeof body !== 'object') return null
  const obj = body as Record<string, unknown>
  if (typeof obj.code === 'string' && obj.code.trim()) {
    return obj.code.trim()
  }
  return null
}

function _coerceFieldMessage(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null
  if (Array.isArray(value)) {
    const flat = value
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter((s) => s.length > 0)
    return flat.length > 0 ? flat.join(', ') : null
  }
  return null
}
